import { useState, useCallback, useRef } from 'react';
import { usePlatform } from '../shared/usePlatform';
import { useHapticFeedback } from './useHapticFeedback';
import { getPlatformConfig } from '../../utils/shared/platformUtils';

/**
 * Mobile-specific touch gestures hook
 * Provides swipe, pinch, and long press functionality
 */
export const useTouchGestures = () => {
  const { isMobile } = usePlatform();
  const { hapticSwipe, hapticLongPress } = useHapticFeedback();
  const config = getPlatformConfig('mobile');
  
  const [gestureState, setGestureState] = useState({
    isSwiping: false,
    isPinching: false,
    isLongPressing: false,
  });
  
  const touchRef = useRef({
    startX: 0,
    startY: 0,
    startTime: 0,
    startDistance: 0,
    longPressTimer: null,
  });

  // Swipe detection
  const handleSwipe = useCallback((direction, distance, velocity) => {
    if (!isMobile) return;
    
    hapticSwipe(direction);
    
    // Trigger swipe callback
    if (onSwipe) {
      onSwipe(direction, distance, velocity);
    }
  }, [isMobile, hapticSwipe]);

  // Long press detection
  const handleLongPress = useCallback((x, y) => {
    if (!isMobile) return;
    
    hapticLongPress();
    setGestureState(prev => ({ ...prev, isLongPressing: true }));
    
    // Trigger long press callback
    if (onLongPress) {
      onLongPress(x, y);
    }
  }, [isMobile, hapticLongPress]);

  // Touch start handler
  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    const now = Date.now();
    
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: now,
      startDistance: 0,
      longPressTimer: setTimeout(() => {
        handleLongPress(touch.clientX, touch.clientY);
      }, 500), // 500ms for long press
    };
    
    // Handle multi-touch for pinch
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      touchRef.current.startDistance = distance;
      setGestureState(prev => ({ ...prev, isPinching: true }));
    }
  }, [isMobile, handleLongPress]);

  // Touch move handler
  const handleTouchMove = useCallback((e) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const deltaX = currentX - touchRef.current.startX;
    const deltaY = currentY - touchRef.current.startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Cancel long press if moved too much
    if (distance > 10 && touchRef.current.longPressTimer) {
      clearTimeout(touchRef.current.longPressTimer);
      touchRef.current.longPressTimer = null;
    }
    
    // Handle swipe detection
    if (distance > config.dragThreshold && !gestureState.isSwiping) {
      setGestureState(prev => ({ ...prev, isSwiping: true }));
    }
    
    // Handle pinch detection
    if (e.touches.length === 2 && gestureState.isPinching) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      const scale = currentDistance / touchRef.current.startDistance;
      
      // Trigger pinch callback
      if (onPinch) {
        onPinch(scale);
      }
    }
  }, [isMobile, config.dragThreshold, gestureState.isSwiping, gestureState.isPinching]);

  // Touch end handler
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile) return;
    
    const now = Date.now();
    const deltaTime = now - touchRef.current.startTime;
    const deltaX = e.changedTouches[0].clientX - touchRef.current.startX;
    const deltaY = e.changedTouches[0].clientY - touchRef.current.startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const velocity = distance / deltaTime;
    
    // Clear long press timer
    if (touchRef.current.longPressTimer) {
      clearTimeout(touchRef.current.longPressTimer);
    }
    
    // Handle swipe completion
    if (gestureState.isSwiping && distance > 50) {
      const direction = Math.abs(deltaX) > Math.abs(deltaY) 
        ? (deltaX > 0 ? 'right' : 'left')
        : (deltaY > 0 ? 'down' : 'up');
      
      handleSwipe(direction, distance, velocity);
    }
    
    // Reset gesture state
    setGestureState({
      isSwiping: false,
      isPinching: false,
      isLongPressing: false,
    });
    
    // Reset touch ref
    touchRef.current = {
      startX: 0,
      startY: 0,
      startTime: 0,
      startDistance: 0,
      longPressTimer: null,
    };
  }, [isMobile, gestureState.isSwiping, handleSwipe]);

  // Callback setters
  let onSwipe = null;
  let onLongPress = null;
  let onPinch = null;

  const setSwipeCallback = useCallback((callback) => {
    onSwipe = callback;
  }, []);

  const setLongPressCallback = useCallback((callback) => {
    onLongPress = callback;
  }, []);

  const setPinchCallback = useCallback((callback) => {
    onPinch = callback;
  }, []);

  return {
    // Event handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    
    // Callback setters
    setSwipeCallback,
    setLongPressCallback,
    setPinchCallback,
    
    // Gesture state
    gestureState,
    
    // Utility functions
    isMobile,
    config,
  };
};
